/* © CNES */
import { TabMenu } from 'primereact/tabmenu';
import { Tooltip } from 'primereact/tooltip';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from '../../../context/ConfigContext';
import { getTooltipMessage } from '../../shared/Utils';
import './NavigationContainer.scss';

/**
 * Navigation Container to display navigation container in application header.
 * @param tabindex defines which page from router is currently active.
 * @returns NavigationContainer Element.
 */
export const NavigationContainer = ({ tabindex }: { tabindex: number }) => {
  const navigate = useNavigate();
  const context = useContext(ConfigContext);
  const isAuthDisabled =
    process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test';

  const isUserNotLogged: boolean = useMemo(() => {
    return !isAuthDisabled && !context.userLoggedIn.email;
  }, [context.userLoggedIn.email, isAuthDisabled]);
  const items = useMemo(() => {
    const itemsList = [
      {
        label: 'Explore',
        icon: 'pi pi-fw pi-globe',
        command: () => {
          navigate('/');
        },
      },
      {
        className: 'target-my-data-disabled-menu',
        label: 'My Data',
        icon: 'pi pi-fw pi-shopping-cart',
        command: () => {
          navigate('/myData');
        },
        disabled: isUserNotLogged,
      },
      {
        label: 'Help',
        icon: 'pi pi-fw pi-question-circle',
        command: () => {
          window.open(window.__RUNTIME_CONFIG__?.HELP_URL, '_blank');
        },
      },
      {
        label: 'Contact',
        icon: 'pi pi-fw pi-envelope',
        command: () => {
          window.open(window.__RUNTIME_CONFIG__?.CONTACT_URL, '_blank');
        },
      },
    ];

    return itemsList;
  }, [navigate, isUserNotLogged]);

  const [activeIndex, setActiveIndex] = useState(0);

  /**
   * setActiveIndex on tabindex change.
   */
  useEffect(() => {
    setActiveIndex(tabindex);
  }, [tabindex]);

  return (
    <>
      <Tooltip
        target=".target-my-data-disabled-menu"
        content={getTooltipMessage(isUserNotLogged)}
        position={'bottom'}
      />
      <TabMenu
        model={items}
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      />
    </>
  );
};
